import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { TID } from "@/lib/testIds";
import pickRandom, { PKW_IMAGES, LKW_IMAGES, AGRAR_IMAGES } from "@/lib/randomImage";

const CATS = [
  {
    key: "pkw",
    n: "01",
    title: "PKW",
    tag: "Sport & Alltag",
    desc: "Vom Kleinwagen bis zum Hochleistungssportler — mehr Drehmoment, sauberere Gasannahme und optimierte Effizienz.",
    span: "md:col-span-7",
    img: pickRandom(PKW_IMAGES),
  },
  {
    key: "lkw",
    n: "02",
    title: "LKW",
    tag: "Nutzfahrzeuge",
    desc: "Weniger Verbrauch, mehr Durchzug. Optimierte Files für Fern- und Verteilerverkehr.",
    span: "md:col-span-5",
    img: pickRandom(LKW_IMAGES),
  },
  {
    key: "agrar",
    n: "03",
    title: "AGRAR",
    tag: "Landmaschinen & Traktoren",
    desc: "Maximale Zugkraft und Effizienz für Traktoren, Erntemaschinen und Nutzfahrzeuge in der Landwirtschaft.",
    span: "md:col-span-12",
    img: pickRandom(AGRAR_IMAGES),
  },
];

export default function VehicleCategories() {
  return (
    <section id="vehicles-section" data-testid={TID.vehicles.root} className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="max-w-2xl mb-14">
          <p className="text-sm uppercase tracking-[0.35em] text-primary mb-4">Fahrzeugklassen</p>
          <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Für jede Maschine<br />das <span className="text-primary">richtige File</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          {CATS.map((c, i) => (
            <motion.div
              key={c.key}
              data-testid={TID.vehicles.card(c.key)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className={`group relative overflow-hidden border border-white/10 hover:border-primary/40 transition-colors duration-500 ${c.span} ${
                c.key === "agrar" ? "h-[360px]" : "h-[460px]"
              }`}
            >
              <img
                src={c.img}
                alt={`${c.title} Chiptuning`}
                className="absolute inset-0 h-full w-full object-cover grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-90 group-hover:scale-105 transition-all duration-[900ms] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="absolute top-6 left-6 font-display font-bold text-2xl text-white/30">{c.n}</div>
              <div className="absolute bottom-0 left-0 right-0 p-7 md:p-9">
                <span className="inline-block px-3 py-1 bg-white/5 border border-white/10 text-[11px] font-bold uppercase tracking-widest text-primary mb-4">
                  {c.tag}
                </span>
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <h3 className="font-display font-bold uppercase tracking-tight text-4xl md:text-5xl text-white">
                      {c.title}
                    </h3>
                    <p className="mt-3 max-w-md text-sm md:text-base text-white/55 leading-relaxed">{c.desc}</p>
                  </div>
                  <div className="shrink-0 h-12 w-12 grid place-items-center border border-white/20 group-hover:bg-primary group-hover:border-primary transition-colors duration-300">
                    <ArrowUpRight className="h-5 w-5 text-white group-hover:text-black transition-colors" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
